import { Button } from "pepsico-ds";
import PropTypes from "prop-types";
import { useState } from "react";
import { FeatureToggle, useFeatureToggle } from "../../utils/featureToggle";
import FileManagerModal from "./FileManagerModal";

/**
 * Reusable File History Button Component
 *
 * A common component that provides a "File History" button which opens the FileManagerModal.
 * Can be used across different modules/features to provide consistent file history access.
 *
 * @example
 * // Basic usage
 * <FileHistoryButton
 *   contentType="LEADERBOARD"
 * />
 *
 * @example
 * // With custom button text and variant
 * <FileHistoryButton
 *   contentType="CONSUMER_REWARD"
 *   buttonText="View Import/Export History"
 *   variant="secondary"
 *   size="small"
 * />
 *
 * @example
 * // Show only exports
 * <FileHistoryButton
 *   contentType="CODE_GROUP"
 *   operationType="EXPORT"
 * />
 *
 * @example
 * // Programmatic control
 * <FileHistoryButton
 *   contentType="LEADERBOARD"
 *   isOpen={externalModalState}
 *   onClose={() => setExternalModalState(false)}
 * />
 */
const FileHistoryButton = ({
  contentType = "text/csv",
  operationType = "ALL",
  buttonText = "View File History",
  variant = "primary",
  size = "medium",
  disabled = false,
  className = "",
  isOpen: externalIsOpen,
  onClose: externalOnClose,
  onOpen,
  ...buttonProps
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const useLegacyExportImport = useFeatureToggle(
    FeatureToggle.useLegacyExportImport
  );

  // Use external state if provided, otherwise use internal state
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen =
    externalOnClose !== undefined
      ? (value) => {
          if (!value) externalOnClose();
        }
      : setInternalIsOpen;

  const handleButtonClick = () => {
    setIsOpen(true);
    onOpen?.();
  };

  const handleModalClose = () => {
    setIsOpen(false);
  };

  if (useLegacyExportImport) {
    return null;
  }

  return (
    <>
      <Button
        variant={variant}
        size={size}
        disabled={disabled}
        className={className}
        onClick={handleButtonClick}
        {...buttonProps}
        iconLeading="history"
      >
        {buttonText}
      </Button>

      <FileManagerModal
        contentType={contentType}
        operationType={operationType}
        isOpen={isOpen}
        onClose={handleModalClose}
      />
    </>
  );
};

FileHistoryButton.propTypes = {
  /** Content type for filtering files (LEADERBOARD, CONSUMER_REWARD, CODE_GROUP, etc.) */
  contentType: PropTypes.string.isRequired,

  /** Operation type filter: 'ALL', 'EXPORT', or 'IMPORT' */
  operationType: PropTypes.oneOf(["ALL", "EXPORT", "IMPORT"]),

  /** Text to display on the button */
  buttonText: PropTypes.string,

  /** Button variant from pepsico-ds */
  variant: PropTypes.oneOf(["primary", "secondary", "tertiary", "text"]),

  /** Button size from pepsico-ds */
  size: PropTypes.oneOf(["small", "medium", "large"]),

  /** Whether the button is disabled */
  disabled: PropTypes.bool,

  /** Additional CSS class for the button */
  className: PropTypes.string,

  /** External control: modal open state */
  isOpen: PropTypes.bool,

  /** External control: callback when modal should close */
  onClose: PropTypes.func,

  /** Callback when modal is opened */
  onOpen: PropTypes.func,
};

export default FileHistoryButton;
